// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CloudDoneOutlined,
  CloudUploadOutlined,
  Upload,
} from '@/constants/hardfin-standard-icons';
import { formatDuration } from '@/utilities/format-duration';
import { formatNumber } from '@/utilities/number';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

interface CsvUploadProps {
  uploadErrorMessage: string | null;
  file: File | null;
  isUploading: boolean;
  onSelectFile: () => void;
  onUpload: () => void;
  onBack: () => void;
  onClose?: () => void;
  hasFatalError?: boolean;
  uploadLineCount?: number;
}

const ESTIMATED_RECORDS_PER_SECOND = 10;
const ESTIMATED_UPLOAD_BUFFER_SECONDS = 2;
const UPLOAD_COUNTDOWN_INTERVAL_MS = 1000;

export function CsvUpload(props: CsvUploadProps) {
  const estimatedUploadTime = props.uploadLineCount
    ? Math.round(props.uploadLineCount / ESTIMATED_RECORDS_PER_SECOND) +
      ESTIMATED_UPLOAD_BUFFER_SECONDS
    : 0;
  const [countdown, setCountdown] = useState(0);

  const handleUpload = () => {
    props.onUpload();
    setCountdown(estimatedUploadTime);
  };

  useEffect(() => {
    if (!props.isUploading) {
      setCountdown(0);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prevCount) => Math.max(prevCount - 1, 0));
    }, UPLOAD_COUNTDOWN_INTERVAL_MS);

    return () => {
      clearInterval(timer);
    };
  }, [props.isUploading]);

  return (
    <Card>
      <CardContent>
        {props.uploadErrorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {props.uploadErrorMessage}
          </Alert>
        )}

        {props.hasFatalError ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Upload failed</AlertTitle>
            Something went wrong with your upload, support has been notified.
          </Alert>
        ) : (
          <>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                mb: 3,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'grey.50',
                },
              }}
              onClick={props.onSelectFile}
            >
              {props.file ? (
                <CloudDoneOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              ) : (
                <CloudUploadOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              )}
              <Typography variant="h6" gutterBottom>
                {props.file
                  ? props.file.name
                  : 'Upload a CSV file with your shipment data'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {props.file
                  ? `Ready to upload ${formatNumber(props.uploadLineCount ?? 0)} shipments (${formatNumber(Math.round(props.file.size / 1024))} KB)`
                  : 'Click to browse for your CSV file'}
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Warning about shipment data updates</AlertTitle>
              <Box
                component="ul"
                sx={{ pl: 3, mt: 2, mb: 1, '& li + li': { mt: 0.5 } }}
              >
                <li>This upload updates existing shipments only</li>
                <li>Shipment number must match an existing shipment</li>
                <li>
                  Blank shipping cost cells clear the existing shipping cost
                  record from those shipments
                </li>
                <li>
                  Delete the shipping cost column to leave existing shipping
                  costs unchanged
                </li>
                <li>
                  Shipping cost values cannot include more than two decimal
                  places
                </li>
              </Box>
            </Alert>
          </>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignContent="center"
        >
          <Button
            variant="outlined"
            onClick={props.onBack}
            disabled={props.isUploading}
          >
            Back
          </Button>
          <Box display="flex" alignItems="center">
            {props.uploadLineCount !== undefined && !props.isUploading && (
              <Typography color="text.secondary">
                Estimated processing time: {formatDuration(estimatedUploadTime)}
              </Typography>
            )}
            {props.isUploading && countdown > 0 && (
              <Typography color="text.secondary">
                Estimated time remaining: {formatDuration(countdown)}
              </Typography>
            )}
          </Box>
          {props.hasFatalError && props.onClose ? (
            <Button variant="contained" onClick={props.onClose}>
              Close
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={
                props.isUploading ? <CircularProgress size={20} /> : <Upload />
              }
              onClick={handleUpload}
              disabled={!props.file || props.isUploading}
            >
              {props.isUploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
